import type React from "react";
import { useState } from "react";
import { Button, Menu, MenuItem, Typography, Paper, Stack, styled, Link, type MenuItemProps } from "~/common/mui";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import BuildOutlinedIcon from "@mui/icons-material/BuildOutlined";
import { useTranslation } from "react-i18next";

interface ToolItem {
  id: string;
  label: string;
  url: string;
}

interface Props {
  config: ToolItem[];
}

export const PlpLinearConfigurator = ({ config }: Props) => {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const isSingleOption = config.length === 1;

  return (
    <StyledPaper elevation={0}>
      <Stack direction="row" alignItems="center" spacing={1}>
        <BuildOutlinedIcon sx={{ marginLeft: "12px" }} />
        <Typography variant="bodyLarge">{t("configure.linear.solution.text")}</Typography>
      </Stack>

      {isSingleOption ? (
        <Button size={"medium"} href={config[0].url}>
          {t("launch.configurator.name.button.text", { label: `${config[0].label}` })}
        </Button>
      ) : (
        <>
          <Button
            size={"medium"}
            onClick={handleClick}
            endIcon={open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          >
            {t("launch.configurator.name.button.text", { label: "" })}
          </Button>
          <Menu
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            PaperProps={{
              component: StyledMenuPaper,
            }}
          >
            {config.map((item) => (
              <StyledMenuItem
                key={item.id}
                {...({
                  component: StyledLink,
                  href: item.url,
                } as MenuItemProps)}
              >
                {t(item.label)}
              </StyledMenuItem>
            ))}
          </Menu>
        </>
      )}
    </StyledPaper>
  );
};

const StyledPaper = styled(Paper)(({ theme }) => ({
  background: theme.palette.primary.container,
  color: theme.palette.primary.onContainer,
  padding: "8px",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  borderRadius: "28px",
  gap: "8px",
}));

const StyledMenuItem = styled(MenuItem)({
  padding: "0 12px",
});

const StyledLink = styled(Link)(({ theme }) => ({
  color: theme.palette.text.primary,
  display: "flex",
  height: "56px",
  alignItems: "center",
  gap: "12px",
  "&:hover": {
    textDecoration: "none",
  },
  width: "100%",
}));

const StyledMenuPaper = styled(Paper)(({ theme }) => ({
  marginTop: "8px !important",
  width: 209,
  borderRadius: "8px",
  boxShadow: theme.shadows[2],
  backgroundColor: theme.palette.background.default,
}));

export default PlpLinearConfigurator;
